import { isValidElement, useState, type MouseEvent, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { Icon, type IconName } from '../../icons/Icon';
import { Button } from '../../primitives/Button/Button';
import { ErrorState } from '../../primitives/ErrorState/ErrorState';
import { useDsT } from '../../services/i18n';

export type ErrorPageVariant = '403' | '404' | '500' | 'offline' | 'maintenance';

export interface ErrorPageAction {
  label: string;
  onClick?: () => void;
  href?: string;
  icon?: IconName;
}

export interface ErrorPageLinkProps {
  href: string;
  className: string;
  children: ReactNode;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
}

export interface ErrorPageProps {
  variant: ErrorPageVariant;
  /** Overrides the variant's default title. */
  title?: ReactNode;
  /** Overrides the variant's default explanation. */
  description?: ReactNode;
  icon?: IconName;
  /** The one recovery action (a button or link spec, or any node). */
  primaryAction?: ErrorPageAction | ReactNode;
  secondaryAction?: ErrorPageAction | ReactNode;
  /** Support reference, rendered with a copy control. */
  referenceId?: string;
  referenceLabel?: string;
  /** Extra detail behind "Technical details" (permission codes, the failing address). */
  technical?: ReactNode;
  technicalLabel?: string;
  /** Router integration for link actions; defaults to a plain anchor. */
  renderLink?: (props: ErrorPageLinkProps) => ReactNode;
  /** `page` (default) centres the block in the content area; `inline` fits a panel. */
  layout?: 'page' | 'inline';
  className?: string;
}

interface VariantCopy {
  icon: IconName;
  title: string;
  description: string;
  tone: 'neutral' | 'critical';
}

export const errorPageDefaults: Record<ErrorPageVariant, VariantCopy> = {
  '403': { icon: 'lock', title: "You don't have access", description: 'Your role does not include this screen. Ask an administrator if you need it.', tone: 'neutral' },
  '404': { icon: 'search', title: 'Page not found', description: 'The address may be wrong, or the page may have moved.', tone: 'neutral' },
  '500': { icon: 'alert-circle', title: 'Something went wrong', description: 'The page could not be shown. Nothing was changed. Try again, or continue with other work.', tone: 'critical' },
  offline: { icon: 'cloud-off', title: 'You are offline', description: 'This page needs a connection. It will load again once the connection returns.', tone: 'neutral' },
  maintenance: { icon: 'wrench', title: 'Zagru One is being updated', description: 'The platform is briefly unavailable for maintenance. Try again in a few minutes.', tone: 'neutral' },
};

/** Catalogue keys of each variant's copy. */
const errorPageKeys: Record<ErrorPageVariant, { title: string; description: string }> = {
  '403': { title: 'ds.errorPage.forbiddenTitle', description: 'ds.errorPage.forbiddenDescription' },
  '404': { title: 'ds.errorPage.notFoundTitle', description: 'ds.errorPage.notFoundDescription' },
  '500': { title: 'ds.errorPage.serverErrorTitle', description: 'ds.errorPage.serverErrorDescription' },
  offline: { title: 'ds.errorPage.offlineTitle', description: 'ds.errorPage.offlineDescription' },
  maintenance: { title: 'ds.errorPage.maintenanceTitle', description: 'ds.errorPage.maintenanceDescription' },
};

function isActionSpec(action: ErrorPageProps['primaryAction']): action is ErrorPageAction {
  return action != null && typeof action === 'object' && !isValidElement(action) && 'label' in (action as object);
}

/** Error archetype: icon tile, title, explanation, primary recovery action, secondary action and a support reference. */
export function ErrorPage({
  variant,
  title,
  description,
  icon,
  primaryAction,
  secondaryAction,
  referenceId,
  referenceLabel,
  technical,
  technicalLabel: technicalLabelProp,
  renderLink,
  layout = 'page',
  className,
}: ErrorPageProps) {
  const t = useDsT();
  const technicalLabel = technicalLabelProp ?? t('ds.errorState.technical', 'Technical details');
  const defaults = errorPageDefaults[variant];
  const link = (props: ErrorPageLinkProps) => {
    if (renderLink) return renderLink(props);
    const { children, ...rest } = props;
    return <a {...rest}>{children}</a>;
  };

  const renderAction = (action: ErrorPageProps['primaryAction'], kind: 'primary' | 'secondary'): ReactNode => {
    if (action == null) return null;
    if (!isActionSpec(action)) return action;
    const content = (
      <>
        {action.icon ? <Icon name={action.icon} size={16} /> : null}
        {action.label}
      </>
    );
    if (action.href) {
      return link({
        href: action.href,
        className: cn(
          'zo-button inline-flex h-control items-center gap-1.5 rounded-md px-3 type-label transition-colors motion-fast focus-ring',
          kind === 'primary' ? 'bg-primary text-primary-fg hover:bg-primary-hover' : 'border border-border bg-surface-1 text-fg hover:bg-hover',
        ),
        children: content,
        onClick: action.onClick
          ? (e) => {
              e.preventDefault();
              action.onClick?.();
            }
          : undefined,
      });
    }
    return (
      <Button variant={kind} size="md" onClick={action.onClick} iconStart={action.icon ? <Icon name={action.icon} size={16} /> : undefined}>
        {action.label}
      </Button>
    );
  };

  const primary = renderAction(primaryAction, 'primary');
  const secondary = renderAction(secondaryAction, 'secondary');

  return (
    <div data-variant={variant} className={cn('zo-error-page flex w-full flex-col', layout === 'page' && 'min-h-full flex-1 items-center justify-center p-6', className)}>
      <ErrorState
        variant="page"
        tone={defaults.tone}
        icon={icon ?? defaults.icon}
        headingLevel={2}
        title={title ?? t(errorPageKeys[variant].title, defaults.title)}
        description={description ?? t(errorPageKeys[variant].description, defaults.description)}
        referenceId={referenceId}
        referenceLabel={referenceLabel}
        action={
          primary || secondary ? (
            <div className="flex flex-wrap items-center justify-center gap-2">
              {secondary}
              {primary}
            </div>
          ) : undefined
        }
        className="my-0"
      />
      {technical ? <TechnicalDisclosure label={technicalLabel}>{technical}</TechnicalDisclosure> : null}
    </div>
  );
}

function TechnicalDisclosure({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 flex max-w-md flex-col items-center type-caption text-fg-subtle">
      <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)} className="inline-flex min-h-6 items-center gap-1 rounded-sm text-fg-muted hover:text-fg focus-ring">
        <Icon name="chevron-right" size={12} className={cn('transition-transform motion-fast', open && 'rotate-90')} />
        {label}
      </button>
      {open ? <div className="mt-1.5 text-center">{children}</div> : null}
    </div>
  );
}
